import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import clockIcon from "@/assets/Icons/Clock.png";
import chat1Icon from "@/assets/Icons/Chat1.png";
import chat2Icon from "@/assets/Icons/Chat2.png";
import cogwheelIcon from "@/assets/Icons/Cogwheel.png";
import heroBg from "@/assets/hero-bg-new.png";
import chatFulltruiEn from "@/assets/ChatFulltrui.png";
import spjallFulltruiIs from "@/assets/SpjallFulltrui.png";
import { AnimatedSection } from "@/components/AnimatedSection";

const ChatAssistant = () => {
  const { language } = useLanguage();

  const content = {
    is: {
      title: "SpjallFulltrúi",
      subtitle: "Gervigreindardrifinn spjallþjónn sem vinnur 24/7",
      description: "Fulltrúi býður upp á fullkomlega sjálfvirka spjallþjóna sem geta svarað spurningum viðskiptavina, veitt þjónustu og lokað sölu allan sólarhringinn.",
      features: [
        {
          icon: clockIcon,
          title: "Alltaf á vaktinni",
          description: "SpjallFulltrúi sefur aldrei - hann er tilbúinn að aðstoða viðskiptavini allan sólarhringinn allt árið.",
        },
        {
          icon: chat2Icon,
          title: "Náttúruleg og samhengisrík samtöl",
          description: "Skilur samtal og samhengi til að veita nákvæm, gagnleg svör í mannlegu spjalli sem byggir traust.",
        },
        {
          icon: cogwheelIcon,
          title: "Sérsniðin fyrir þitt fyrirtæki",
          description: "Aðlagast vörumerkinu þínu, stefnum og þörfum til að veita samræmda upplifun.",
        },
      ],
    },
    en: {
      title: "Chat Assistant",
      subtitle: "AI-powered chatbot that works 24/7",
      description: "Fulltrúi offers fully automated chatbots that can answer customer questions, provide service, and close sales around the clock.",
      features: [
        {
          icon: clockIcon,
          title: "Always On Duty",
          description: "Chat Assistant never sleeps - ready to help your customers around the clock, all year round.",
        },
        {
          icon: chat2Icon,
          title: "Natural & Contextual Conversations",
          description: "Understands conversation context to provide accurate, helpful responses in human-like dialogue that builds trust.",
        },
        {
          icon: cogwheelIcon,
          title: "Customized for Your Business",
          description: "Adapts to your brand, policies, and needs to provide a consistent experience.",
        },
      ],
    },
  };

  const t = content[language];

  return (
    <main className="min-h-screen">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${heroBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-background/65 via-background/60 to-background" />
        </div>

        <div className="container px-6 md:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center animate-fade-in">
            <img
              src={chat1Icon}
              alt={t.title}
              className="h-32 w-32 mx-auto mb-6 object-contain"
              loading="lazy"
            />
            <h1 className="mb-6">{t.title}</h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8">
              {t.subtitle}
            </p>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t.description}
            </p>
          </div>
        </div>
      </section>

      {/* Illustration Section */}
      <AnimatedSection>
        <section className="py-20">
          <div className="container px-6 md:px-8">
            <div className="max-w-7xl mx-auto">
              <img 
                src={language === 'is' ? spjallFulltruiIs : chatFulltruiEn} 
                alt={language === 'is' ? 'Spjallfulltrúi' : 'Chat Fulltrúi'}
                className="w-full h-auto"
              />
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Features Section */}
      <AnimatedSection delay={0.1}>
        <section className="py-20">
          <div className="container px-6 md:px-8">
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {t.features.map((feature, index) => (
                <div
                  key={index}
                  className="p-8 rounded-lg border border-border bg-card hover:shadow-lg transition-all animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <img
                    src={feature.icon}
                    alt={feature.title}
                    className="h-24 w-24 mb-4 object-contain"
                    loading="lazy"
                  />
                  <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      <Footer />
    </main>
  );
};

export default ChatAssistant;
